import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Footer, MarketingFinalCta, SectionHeading } from '../components/homepage';
import { DEMO_REQUEST_MAILTO } from '../config/contact';
import {
  marketingCardSx,
  marketingDarkSectionSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

const heroSummaryMetrics = [
  {
    label: 'Profiles analyzed',
    value: '20+',
    detail: 'Top priority gaps surfaced',
    accent: '#0f6fff',
  },
  {
    label: 'Decision gap',
    value: 'Visible',
    detail: 'Better paths available',
    accent: '#4f46e5',
  },
  {
    label: 'Control model',
    value: '3-way',
    detail: 'Approval, economics, risk',
    accent: '#b45309',
  },
];

const heroProfiles = [
  {
    title: 'Cross-border mobile wallet demand',
    context: 'Higher relevance available',
    summary:
      'Wallet-first international traffic is following weaker default paths than it has to.',
    chips: [
      { label: 'Approval', value: 'Up', color: '#0f6fff' },
      { label: 'Economics', value: 'Watch FX', color: '#4f46e5' },
    ],
  },
  {
    title: 'Domestic repeat-buyer card flow',
    context: 'Retry quality matters',
    summary:
      'Recoverable failures are clustering into retry paths that are not strong enough.',
    chips: [
      { label: 'Retries', value: 'Upgrade', color: '#4f46e5' },
      { label: 'Risk', value: 'Stable', color: '#b45309' },
    ],
  },
  {
    title: 'Higher-risk card traffic',
    context: 'Precision needed',
    summary:
      'Risk posture is too broad for some transactions and not targeted enough for others.',
    chips: [
      { label: 'Risk', value: 'Tighten', color: '#b45309' },
      { label: 'Approval', value: 'Protect', color: '#0f6fff' },
    ],
  },
];

const heroContextNotes = [
  {
    title: 'Why these profiles matter',
    copy: 'The same stack can produce very different outcomes once customer context, provider behavior, and risk posture change.',
  },
  {
    title: 'What the health check shows',
    copy: 'Ormal highlights which payment profiles are underperforming and where better decisions are already available.',
  },
  {
    title: 'How teams use it',
    copy: 'Payments, finance, risk, and product get one shared view of what is happening before any production changes are made.',
  },
];

const patternCards = [
  {
    title: 'The approval gap in your data is wider than your routing knows.',
    copy:
      'Better paths are available for some of your transactions right now. Your stack cannot see them because routing decisions were set before those paths existed.',
    label: 'Approval opportunity',
    accent: '#0f6fff',
    cause:
      'Provider fit, routing order, and retry timing are often weaker than the payment context requires.',
    help:
      'Ormal shows where a different route, method, or recovery path would have given the transaction a better chance to clear.',
    bars: [
      { label: 'Current path', width: '46%', color: 'rgba(15,111,255,0.28)' },
      { label: 'Better available path', width: '78%', color: '#0f6fff' },
    ],
  },
  {
    title: 'The same basket can clear two ways. Your stack does not know which one wins.',
    copy:
      'Provider performance, method economics, and risk profile all vary by context. Static routing picks one path and applies it — not the best one for this transaction.',
    label: 'Decision variability',
    accent: '#4f46e5',
    cause:
      'Static rules treat unlike payment contexts too similarly, even when provider behavior and economics are moving.',
    help:
      'Ormal makes the tradeoff visible so teams can see where the stack should have chosen a different path.',
    bars: [
      { label: 'Provider A', width: '74%', color: '#4f46e5' },
      { label: 'Provider B', width: '52%', color: 'rgba(79,70,229,0.34)' },
    ],
  },
  {
    title: 'Broad risk rules are blocking good customers. You cannot see which ones.',
    copy:
      'Blanket friction reduces conversion on segments that were never the problem. Ormal shows which rules are over-firing and where precision can recover demand without adding exposure.',
    label: 'Risk calibration',
    accent: '#b45309',
    cause:
      'Risk posture drifts over time, while many stacks keep applying the same broad controls across very different transactions.',
    help:
      'Ormal shows where risk decisions lack precision and where friction can be reduced without ignoring exposure.',
    bars: [
      { label: 'Good demand blocked', width: '62%', color: '#f59e0b' },
      { label: 'Precision needed', width: '38%', color: 'rgba(180,83,9,0.28)' },
    ],
  },
  {
    title: "Your provider's approval rate last week isn't what it is right now.",
    copy:
      'Provider performance shifts by corridor, method, and time of day. Routing decisions made at setup do not account for the drift. Ormal measures provider performance where it matters — on your corridors, your methods, your history — and re-authors routing when the drift becomes real.',
    label: 'Provider intelligence',
    accent: '#0f766e',
    cause:
      'Provider performance changes across regions, payment methods, and traffic conditions faster than many teams can monitor manually.',
    help:
      'Ormal surfaces where provider behavior is shifting so teams can respond before the drift becomes a business problem.',
    bars: [
      { label: 'Morning window', width: '68%', color: '#0f766e' },
      { label: 'Evening window', width: '49%', color: 'rgba(15,118,110,0.3)' },
    ],
  },
];

const healthCheckColumns = [
  {
    title: 'What we analyze',
    items: [
      'Transaction history',
      'Provider behavior',
      'Retry patterns',
      'Risk outcomes',
    ],
  },
  {
    title: 'What we surface',
    items: [
      'Where decisions underperform',
      'Where better paths exist',
      'Where tradeoffs are misaligned',
    ],
  },
  {
    title: 'What teams leave with',
    items: [
      'Prioritized improvement areas',
      'Shared visibility',
      'A clear starting point',
    ],
  },
];

const heroProfileData = [
  {
    profile: 'Cross-border wallet',
    context: 'Approval gap',
    accent: '#d97706',
    metric: { label: 'Approval rate', current: 64, target: 76, scale: 100, unit: '%', color: '#0f6fff', improving: true },
    chips: [{ label: 'Cost', ok: true }, { label: 'Risk', ok: true }],
    finding: '+12 pts available',
    findingColor: '#0f6fff',
  },
  {
    profile: 'Domestic retry flow',
    context: 'Recovery weak',
    accent: '#5b7cfa',
    metric: { label: 'Recovery rate', current: 38, target: 56, scale: 100, unit: '%', color: '#4f46e5', improving: true },
    chips: [{ label: 'Cost', ok: true }, { label: 'Risk', ok: true }],
    finding: 'Retry gap wide',
    findingColor: '#4f46e5',
  },
  {
    profile: 'High-risk card traffic',
    context: 'Risk too broad',
    accent: '#b45309',
    metric: { label: 'Risk rate', current: 1.9, target: 0.9, scale: 3, unit: '%', color: '#b45309', improving: false },
    chips: [{ label: 'Cost', ok: false }, { label: 'Approval', ok: false }],
    finding: '−1.0% addressable',
    findingColor: '#b45309',
  },
];

const ResourcesPage: React.FC = () => {
  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href="https://ormal.io/resources" />
      </Helmet>
      <Box
        component="section"
        sx={{
          ...marketingSectionSx,
          pt: { xs: 5.5, md: 7.5 },
          pb: { xs: 6.5, md: 7.5 },
          overflow: 'hidden',
          borderBottom: '1px solid rgba(219,228,239,0.72)',
          background:
            'radial-gradient(860px 520px at 88% 18%, rgba(62,161,255,0.12), transparent 52%), linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)',
        }}
      >
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3, lg: 4 }} alignItems="center">
            <Grid item xs={12} lg={4.9}>
              <SectionHeading
                eyebrow="Insights"
                title="Your payment data already has the answer. You just can't see it yet."
                description="As little as 30 days of transaction history is enough to surface where your stack is leaving approval, cost, and risk performance on the table — 90 days gives us more to work with."
                marginBottom={1.2}
              />
              <Typography
                sx={{
                  color: marketingPalette.body,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.65,
                  mb: 2.1,
                  maxWidth: 440,
                }}
              >
                The Health Check shows you exactly where better decisions are available — before any production change is made.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                <Button
                  component="a"
                  href={DEMO_REQUEST_MAILTO}
                  endIcon={<ArrowForward />}
                  sx={marketingPrimaryButtonSx}
                >
                  Request a demo + health check
                </Button>
                <Button component={RouterLink} to="/white-papers" endIcon={<ArrowForward />} sx={marketingSecondaryButtonSx}>
                  White papers
                </Button>
                <Button component="a" href="#common-patterns" sx={{ ...marketingSecondaryButtonSx, fontSize: '0.82rem' }}>
                  See common patterns ↓
                </Button>
              </Box>

              {/* Proof strip */}
              <Box
                sx={{
                  mt: 3.2,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1.4,
                  width: '100%',
                  maxWidth: 480,
                }}
              >
                {[
                  { value: '30 days', label: 'Transaction data', detail: 'As little as 30 days — 90 preferred' },
                  { value: '1 week',  label: 'To first insight',  detail: 'Quantified opportunity report' },
                  { value: 'Zero',    label: 'Prod changes',      detail: 'No stack change required' },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      pl: 1.3,
                      borderLeft: '2px solid rgba(15,111,255,0.35)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: marketingDisplayFont,
                        fontSize: { xs: '1.05rem', md: '1.18rem' },
                        fontWeight: 400,
                        color: marketingPalette.accent,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 400, color: marketingPalette.muted, lineHeight: 1.3 }}>
                      {s.detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} lg={7.1}>
              <Box
                sx={{
                  p: { xs: 1.6, md: 1.9 },
                  border: '1px solid rgba(196,212,232,0.9)',
                  borderRadius: 1.15,
                  backgroundColor: 'rgba(255,255,255,0.88)',
                  boxShadow: '0 24px 54px rgba(15,23,42,0.07)',
                  backdropFilter: 'blur(12px)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(480px 220px at 18% 10%, rgba(91,124,250,0.07), transparent 56%), radial-gradient(400px 200px at 90% 18%, rgba(217,119,6,0.07), transparent 52%), radial-gradient(300px 180px at 55% 90%, rgba(180,83,9,0.05), transparent 50%)',
                    pointerEvents: 'none',
                  }}
                />

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.4 }}>
                    <Typography sx={{ color: marketingPalette.accent, fontSize: '0.66rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      Health check output
                    </Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 0.9, py: 0.35, borderRadius: 999, border: '1px solid rgba(15,111,255,0.2)', backgroundColor: 'rgba(15,111,255,0.06)' }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#047857', boxShadow: '0 0 6px rgba(4,120,87,0.65)' }} />
                      <Typography sx={{ color: marketingPalette.accent, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                        20+ profiles · 3 highlighted
                      </Typography>
                    </Box>
                  </Box>

                  {/* Profile rows with bar visualization */}
                  <Stack spacing={0.85}>
                    {heroProfileData.map((row) => {
                      const currentPct = Math.min(100, (row.metric.current / row.metric.scale) * 100);
                      const targetPct = Math.min(100, (row.metric.target / row.metric.scale) * 100);
                      return (
                        <Box
                          key={row.profile}
                          sx={{
                            p: { xs: 1.1, md: 1.3 },
                            border: '1px solid rgba(196,212,232,0.82)',
                            borderRadius: 0.9,
                            backgroundColor: '#ffffff',
                          }}
                        >
                          {/* Row header: name + context + finding */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                              <Box sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: row.accent, flexShrink: 0 }} />
                              <Typography sx={{ color: marketingPalette.headline, fontSize: '0.84rem', fontWeight: 400, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                {row.profile}
                              </Typography>
                              <Box sx={{ px: 0.55, py: 0.18, borderRadius: 0.4, backgroundColor: 'rgba(203,213,225,0.45)', border: '1px solid rgba(196,212,232,0.8)', flexShrink: 0 }}>
                                <Typography sx={{ color: marketingPalette.muted, fontSize: '0.58rem', fontWeight: 400, whiteSpace: 'nowrap' }}>
                                  {row.context}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography sx={{ color: row.findingColor, fontSize: '0.74rem', fontWeight: 400, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                              {row.finding}
                            </Typography>
                          </Box>

                          {/* Bar + chips */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1.2, alignItems: 'center' }}>
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                                <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.05em' }}>
                                  {row.metric.label}
                                </Typography>
                                <Typography sx={{ color: marketingPalette.body, fontSize: '0.6rem', fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>
                                  {`${row.metric.current}${row.metric.unit} → target ${row.metric.target}${row.metric.unit}`}
                                </Typography>
                              </Box>
                              {/* Dual-zone bar */}
                              <Box sx={{ position: 'relative', height: 12, borderRadius: 999, backgroundColor: 'rgba(196,212,232,0.32)', overflow: 'hidden' }}>
                                {row.metric.improving ? (
                                  <>
                                    {/* Zone 1: where you are (muted) */}
                                    <Box sx={{
                                      position: 'absolute', left: 0, top: 0, bottom: 0,
                                      width: `${currentPct}%`,
                                      background: `linear-gradient(90deg, ${row.metric.color}55 0%, ${row.metric.color}33 100%)`,
                                      borderRadius: '999px 0 0 999px',
                                    }} />
                                    {/* Zone 2: the opportunity gap (bright) */}
                                    <Box sx={{
                                      position: 'absolute', top: 0, bottom: 0,
                                      left: `${currentPct}%`,
                                      width: `${targetPct - currentPct}%`,
                                      background: `linear-gradient(90deg, ${row.metric.color} 0%, ${row.metric.color}cc 100%)`,
                                      boxShadow: `0 0 10px ${row.metric.color}66`,
                                    }} />
                                  </>
                                ) : (
                                  <>
                                    {/* Zone 1: acceptable zone (green) */}
                                    <Box sx={{
                                      position: 'absolute', left: 0, top: 0, bottom: 0,
                                      width: `${targetPct}%`,
                                      background: 'linear-gradient(90deg, #047857 0%, #04785799 100%)',
                                      borderRadius: '999px 0 0 999px',
                                    }} />
                                    {/* Zone 2: excess (problem, colored) */}
                                    <Box sx={{
                                      position: 'absolute', top: 0, bottom: 0,
                                      left: `${targetPct}%`,
                                      width: `${currentPct - targetPct}%`,
                                      background: `linear-gradient(90deg, ${row.metric.color}cc 0%, ${row.metric.color} 100%)`,
                                      boxShadow: `0 0 10px ${row.metric.color}66`,
                                    }} />
                                  </>
                                )}
                              </Box>
                            </Box>

                            {/* Status chips for other dimensions */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                              {row.chips.map((chip) => (
                                <Box
                                  key={chip.label}
                                  sx={{
                                    px: 0.7, py: 0.28,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: chip.ok ? 'rgba(4,120,87,0.25)' : 'rgba(217,119,6,0.35)',
                                    backgroundColor: chip.ok ? 'rgba(4,120,87,0.07)' : 'rgba(217,119,6,0.08)',
                                  }}
                                >
                                  <Typography sx={{ color: chip.ok ? '#047857' : '#d97706', fontSize: '0.6rem', fontWeight: 400, whiteSpace: 'nowrap' }}>
                                    {chip.label} {chip.ok ? '✓' : '△'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>

                  {/* Bottom note */}
                  <Box sx={{ mt: 1.3, p: 1.05, border: '1px dashed rgba(196,212,232,0.9)', borderRadius: 0.85, backgroundColor: 'rgba(248,250,253,0.74)' }}>
                    <Typography sx={{ color: marketingPalette.body, fontSize: '0.78rem', lineHeight: 1.55 }}>
                      Ormal surfaces where decision quality changes by context — before teams make production changes.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Health check output"
            title="This is what we return after analyzing your transaction history."
            description="One week turnaround. No production changes required. For each of your customer groups: what you actually paid vs what the authored stack prices at today's contracts, how many failed payments it would have recovered at your own observed rates — in dollars, over your own window."
            marginBottom={2.4}
          />
          <Box
            sx={{
              border: '1px solid rgba(196,212,232,0.96)',
              borderRadius: 1.25,
              backgroundColor: '#ffffff',
              boxShadow: '0 28px 64px rgba(15,23,42,0.08)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 2.4 },
                py: 1.15,
                borderBottom: '1px solid rgba(196,212,232,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                backgroundColor: 'rgba(247,250,255,0.96)',
              }}
            >
              <Typography
                sx={{
                  color: marketingPalette.accent,
                  fontSize: '0.68rem',
                  fontWeight: 400,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Analysis view
              </Typography>
              <Typography
                sx={{
                  color: marketingPalette.muted,
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.06em',
                }}
              >
                Historical review
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 2, md: 2.25 } }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: 0.9,
                  mb: 1.1,
                }}
              >
                {heroSummaryMetrics.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1,
                      border: '1px solid rgba(196,212,232,0.88)',
                      borderRadius: 0.85,
                      backgroundColor: '#fbfdff',
                    }}
                  >
                    <Typography
                      sx={{
                        color: item.accent,
                        fontSize: '0.58rem',
                        fontWeight: 400,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        mb: 0.38,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: marketingPalette.headline,
                        fontSize: '0.92rem',
                        fontWeight: 400,
                        lineHeight: 1.2,
                        mb: 0.18,
                      }}
                    >
                      {item.value}
                    </Typography>
                    <Typography sx={{ color: marketingPalette.body, fontSize: '0.68rem', lineHeight: 1.4 }}>
                      {item.detail}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Grid container spacing={0}>
                <Grid item xs={12} md={7.2}>
                  <Box
                    sx={{
                      p: { xs: 1.5, md: 1.7 },
                      border: '1px solid rgba(219,228,239,0.9)',
                      borderRight: { md: 'none' },
                      borderRadius: { xs: '12px 12px 0 0', md: '12px 0 0 12px' },
                      height: '100%',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    <Typography
                      sx={{
                        color: marketingPalette.headline,
                        fontSize: { xs: '1.02rem', md: '1.12rem' },
                        fontWeight: 400,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.14,
                        mb: 0.35,
                      }}
                    >
                      20+ payment profiles analyzed. Top findings surfaced and ranked.
                    </Typography>
                    <Typography sx={{ color: marketingPalette.body, fontSize: '0.76rem', lineHeight: 1.5, mb: 0.95 }}>
                      Each profile shows a different mix of approval, economics, and risk.
                    </Typography>

                    <Stack spacing={0.75}>
                      {heroProfiles.map((profile) => (
                        <Box
                          key={profile.title}
                          sx={{
                            p: 1,
                            border: '1px solid rgba(196,212,232,0.88)',
                            borderRadius: 0.85,
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.35,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  color: marketingPalette.headline,
                                  fontSize: '0.82rem',
                                  fontWeight: 400,
                                  lineHeight: 1.32,
                                  mb: 0.12,
                                }}
                              >
                                {profile.title}
                              </Typography>
                              <Typography
                                sx={{
                                  color: marketingPalette.muted,
                                  fontSize: '0.6rem',
                                  fontWeight: 400,
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {profile.context}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                px: 0.75,
                                py: 0.35,
                                borderRadius: 999,
                                border: '1px solid rgba(196,212,232,0.88)',
                                color: marketingPalette.accent,
                                fontSize: '0.62rem',
                                fontWeight: 400,
                                flexShrink: 0,
                              }}
                            >
                              Likely uplift
                            </Box>
                          </Box>
                          <Typography
                            sx={{
                              color: marketingPalette.body,
                              fontSize: '0.72rem',
                              lineHeight: 1.45,
                              mb: 0.65,
                            }}
                          >
                            {profile.summary}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55 }}>
                            {profile.chips.map((chip) => (
                              <Box
                                key={`${profile.title}-${chip.label}`}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.45,
                                  px: 0.7,
                                  py: 0.38,
                                  borderRadius: 999,
                                  border: '1px solid rgba(196,212,232,0.88)',
                                  backgroundColor: '#fbfdff',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '2px',
                                    backgroundColor: chip.color,
                                  }}
                                />
                                <Typography sx={{ color: marketingPalette.muted, fontSize: '0.62rem', fontWeight: 400 }}>
                                  {chip.label}
                                </Typography>
                                <Typography sx={{ color: marketingPalette.headline, fontSize: '0.66rem', fontWeight: 400 }}>
                                  {chip.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4.8}>
                  <Box
                    sx={{
                      p: { xs: 1.5, md: 1.7 },
                      border: '1px solid rgba(191,219,254,0.12)',
                      borderRadius: { xs: '0 0 12px 12px', md: '0 12px 12px 0' },
                      background: 'linear-gradient(180deg, rgba(16,38,77,0.98) 0%, rgba(20,52,106,0.98) 100%)',
                      color: '#ffffff',
                      height: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(191,219,254,0.88)',
                        fontSize: '0.62rem',
                        fontWeight: 400,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        mb: 0.55,
                      }}
                    >
                      Top-level insight
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        lineHeight: 1.36,
                        letterSpacing: '-0.015em',
                        mb: 0.85,
                      }}
                    >
                      The health check groups historical demand into payment profiles, then shows where decision quality is strongest and weakest.
                    </Typography>
                    <Stack spacing={0.65}>
                      {heroContextNotes.map((item, index) => (
                        <Box
                          key={item.title}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '26px minmax(0, 1fr)',
                            gap: 0.8,
                            alignItems: 'start',
                            p: 0.8,
                            borderRadius: 0.8,
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(191,219,254,0.14)',
                          }}
                        >
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: 0.7,
                              backgroundColor: 'rgba(125,211,252,0.18)',
                              color: '#bfdbfe',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.74rem',
                              fontWeight: 400,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box>
                            <Typography sx={{ color: '#ffffff', fontWeight: 400, fontSize: '0.74rem', mb: 0.14 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: 'rgba(226,232,240,0.86)', fontSize: '0.7rem', lineHeight: 1.42 }}>
                              {item.copy}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box component="section" id="common-patterns" sx={marketingDarkSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Common patterns"
            title="Where payment decisions start to break"
            description="Across payment environments, the same patterns tend to appear once decisions become visible."
            marginBottom={3}
            inverse
          />
          <Grid container spacing={2.5}>
            {patternCards.map((item) => (
              <Grid item xs={12} md={6} key={item.title}>
                <Card sx={{ ...marketingCardSx, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography
                      sx={{
                        color: item.accent,
                        fontSize: '0.68rem',
                        fontWeight: 400,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Box
                      sx={{
                        width: 46,
                        height: 3,
                        borderRadius: 999,
                        backgroundColor: item.accent,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      color: marketingPalette.headline,
                      fontSize: '1.06rem',
                      fontWeight: 400,
                      lineHeight: 1.34,
                      letterSpacing: '-0.015em',
                      mb: 0.8,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.7, mb: 1.35 }}>
                    {item.copy}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 0.8,
                      mb: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        border: '1px solid rgba(196,212,232,0.82)',
                        borderRadius: 0.8,
                        backgroundColor: 'rgba(15,111,255,0.04)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: marketingPalette.accent,
                          fontSize: '0.58rem',
                          fontWeight: 400,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          mb: 0.28,
                        }}
                      >
                        What’s happening → what changes
                      </Typography>
                      <Typography sx={{ color: marketingPalette.body, fontSize: '0.74rem', lineHeight: 1.48 }}>
                        {item.cause} {item.help}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gap: 0.7 }}>
                    {item.bars.map((bar) => (
                      <Box key={bar.label}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.3 }}>
                          <Typography sx={{ color: marketingPalette.muted, fontSize: '0.68rem', fontWeight: 400 }}>
                            {bar.label}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 12,
                            borderRadius: 999,
                            backgroundColor: 'rgba(196,212,232,0.4)',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: bar.width,
                              height: '100%',
                              background: typeof bar.color === 'string' && bar.color.startsWith('rgba')
                                ? bar.color
                                : `linear-gradient(90deg, ${bar.color} 0%, ${bar.color}cc 100%)`,
                              borderRadius: 999,
                              boxShadow: typeof bar.color === 'string' && !bar.color.startsWith('rgba')
                                ? `0 0 10px ${bar.color}55`
                                : 'none',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* THREE THINGS WE MEASURE */}
      <Box component="section" sx={marketingMutedSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="What the health check measures"
            title="Three lenses. As little as thirty days of data. One week to answers."
            description="The health check runs your transaction history through approval, economics, and risk. Every gap is quantified. Every better path is ranked before any production change is made."
            marginBottom={3}
          />
          <Grid container spacing={2.4}>
            {[
              {
                num: '01',
                accent: '#0f6fff',
                soft: 'rgba(15,111,255,0.07)',
                border: 'rgba(15,111,255,0.25)',
                label: 'Approval',
                title: 'Where legitimate demand is not clearing.',
                copy: 'Approval rates shift by route, provider, cohort, and time of day. The health check shows which transactions are following paths weaker than their profile warrants.',
                signals: [
                  'Approval rate by provider vs. baseline',
                  'Retry paths that are underperforming',
                  'Cross-border gaps vs. onshore conversion',
                ],
              },
              {
                num: '02',
                accent: '#4f46e5',
                soft: 'rgba(79,70,229,0.07)',
                border: 'rgba(79,70,229,0.22)',
                label: 'Economics',
                title: 'Where the stack is paying more than it has to.',
                copy: 'Provider cost spread, FX treatment, and method economics shift across your transaction mix. The health check surfaces where blended cost has room to move.',
                signals: [
                  'Provider cost spread for similar traffic',
                  'FX and method economics by corridor',
                  'MID assignment vs. interchange opportunity',
                ],
              },
              {
                num: '03',
                accent: '#b45309',
                soft: 'rgba(180,83,9,0.07)',
                border: 'rgba(180,83,9,0.22)',
                label: 'Risk',
                title: 'Where broad controls are costing you conversion.',
                copy: 'Blanket risk rules over-fire on low-risk segments and drift from changing fraud patterns. The health check shows where precision can recover demand without adding exposure.',
                signals: [
                  'Rules over-firing on low-risk segments',
                  'Posture drift vs. changing fraud patterns',
                  'Friction blocking good demand',
                ],
              },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Card sx={{ ...marketingCardSx, height: '100%', p: { xs: 2.2, md: 2.6 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mb: 1.2 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 0.8,
                        backgroundColor: item.soft,
                        border: `1px solid ${item.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: item.accent,
                        fontFamily: 'monospace',
                        fontSize: '0.76rem',
                        fontWeight: 400,
                        letterSpacing: '0.04em',
                        flexShrink: 0,
                      }}
                    >
                      {item.num}
                    </Box>
                    <Typography sx={{ color: item.accent, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.02rem', md: '1.1rem' }, lineHeight: 1.3, mb: 0.75 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.68, fontSize: '0.88rem', mb: 1.4 }}>
                    {item.copy}
                  </Typography>
                  <Box sx={{ pt: 1.2, borderTop: '1px dashed rgba(196,212,232,0.9)' }}>
                    <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>
                      What we look at
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 0.6 }}>
                      {item.signals.map((signal) => (
                        <Box key={signal} sx={{ display: 'grid', gridTemplateColumns: '12px minmax(0,1fr)', gap: 0.7, alignItems: 'start' }}>
                          <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: item.accent, mt: '0.45rem', flexShrink: 0 }} />
                          <Typography sx={{ color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.55 }}>
                            {signal}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <Card
            sx={{
              ...marketingCardSx,
              p: { xs: 2.5, md: 3.2 },
              background:
                'linear-gradient(135deg, rgba(15,111,255,0.055), rgba(255,255,255,0.98) 42%, rgba(79,70,229,0.045) 100%)',
            }}
          >
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="start">
              <Grid item xs={12} lg={4.8}>
                <SectionHeading
                  eyebrow="Your data"
                  title="See what this looks like in your data"
                  description="The best way to understand payment decision quality is to analyze your own transaction history."
                  marginBottom={1.3}
                />
                <Typography sx={{ color: marketingPalette.body, lineHeight: 1.74, mb: 2.1 }}>
                  In a demo + health check, Ormal reviews your historical payment outcomes, shows where current decisions are underperforming, and highlights where better paths may already be available.
                </Typography>
                <Typography sx={{ color: marketingPalette.headline, lineHeight: 1.68, mb: 2.1 }}>
                  Most teams already have this data. They just do not have visibility into the decisions it represents.
                </Typography>
                <Button
                  component="a"
                  href={DEMO_REQUEST_MAILTO}
                  endIcon={<ArrowForward />}
                  sx={marketingPrimaryButtonSx}
                >
                  Request a demo + health check
                </Button>
              </Grid>

              <Grid item xs={12} lg={7.2}>
                <Grid container spacing={1.4}>
                  {healthCheckColumns.map((column) => (
                    <Grid item xs={12} md={4} key={column.title}>
                      <Box
                        sx={{
                          height: '100%',
                          p: { xs: 1.6, md: 1.8 },
                          border: '1px solid rgba(196,212,232,0.9)',
                          borderRadius: 1,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <Typography
                          sx={{
                            color: marketingPalette.muted,
                            fontSize: '0.62rem',
                            fontWeight: 400,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            mb: 0.9,
                          }}
                        >
                          {column.title}
                        </Typography>
                        <Stack spacing={0.85}>
                          {column.items.map((item) => (
                            <Box
                              key={item}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '10px minmax(0, 1fr)',
                                gap: 0.8,
                                alignItems: 'start',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '2px',
                                  backgroundColor: marketingPalette.accent,
                                  mt: '0.42rem',
                                }}
                              />
                              <Typography sx={{ color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.55 }}>
                                {item}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      <MarketingFinalCta
        kicker="Get started"
        heading="Find out what's in your payment data."
        body="Share as little as 30 days of transaction history — 90 days gives us more signal. One week later you have a quantified opportunity report — or we tell you honestly there isn't one."
        secondary={{ label: 'Request a demo', href: DEMO_REQUEST_MAILTO }}
      />

      <Footer />
    </Box>
  );
};

export default ResourcesPage;
